import {getDsContext} from "@/utils/dsContext";
import {useScoutingReports, reportError} from "@/utils/scoutingReportData";
import ScoutingReportsStatus from "@/components/ScoutingReportsStatus";
import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import moment from "moment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import ScoutPlayerTile from "@/components/ScoutPlayerTile";
import ScoutReportForm from "@/components/ScoutReportForm";
import {PlayerAPI} from "@/api/player";
import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {useScoutingPipeline} from "@/utils/scoutingPipeline";
import {
	defaultReportTemplate,
	reportSummaries,
	saveReport,
	templateSnapshot,
	validateReport,
} from "@/utils/scoutingReports";

import "../index.scss";

const SUGGESTIONS_LIMIT = 12;

const playerSummary = (player) => ({
	id: player.id,
	firstName: player.firstName,
	lastName: player.lastName,
	goalkeeper: !!player.goalkeeper,
	dateOfBirth: player.dateOfBirth || null,
});

// Scelta del giocatore: prima i giocatori in pipeline, poi la ricerca per nome.
function PlayerSearch({onPick, pipeline, summaries, handleNotification}) {
	const seasonId = useSelector((state) => state?.season?.id);
	const [query, setQuery] = useState("");
	const [results, setResults] = useState([]);
	const suggestions = useMemo(
		() =>
			Object.values(pipeline)
				.sort((a, b) => (b.addedAt || "").localeCompare(a.addedAt || ""))
				.slice(0, SUGGESTIONS_LIMIT)
				.map((entry) => entry.player)
				.filter(Boolean),
		[pipeline],
	);
	const [searching, setSearching] = useState(false);

	useEffect(() => {
		if (!query.trim()) {
			setResults([]);
			return;
		}
		let active = true;
		const timer = setTimeout(async () => {
			setSearching(true);
			try {
				const response = await PlayerAPI.paginated({
					prospect: true,
					seasonId,
					search: query.trim(),
					page: 1,
					pageSize: 24,
				});
				if (active) setResults(response?.data || []);
			} catch {
				if (active) handleNotification(ERROR_OPERATION, "error");
			} finally {
				if (active) setSearching(false);
			}
		}, 300);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [query, seasonId, handleNotification]);

	const shown = query.trim() ? results : suggestions;

	return (
		<div className="relazione-search">
			<VLFTextField
				label="Cerca giocatore"
				placeholder="Cognome o nome"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				size="small"
			/>
			<div className="relazione-search__title">
				{query.trim()
					? searching
						? "Ricerca…"
						: results.length
							? "Risultati"
							: "Nessun giocatore trovato"
					: suggestions.length
						? "In pipeline"
						: "Scrivi il nome del giocatore"}
			</div>
			{!searching && !!shown.length && (
				<div className="relazione-search__grid">
					{shown.map((p) => (
						<ScoutPlayerTile
							key={p.id}
							player={p}
							pipelineEntry={pipeline[p.id]}
							reports={summaries[p.id]}
							onClick={() => onPick(p)}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function HandleRelazioneEditor({edit, handleNotification}) {
	const navigate = useNavigate();
	const location = useLocation();
	const {id} = useParams();
	const [searchParams] = useSearchParams();
	const data = useScoutingReports({reportId: edit ? id : undefined});
	const templates = useMemo(
		() => data.templates.filter((t) => t.active !== false),
		[data.templates],
	);
	const {pipeline} = useScoutingPipeline();
	const summaries = useMemo(
		() => reportSummaries(data.reports),
		[data.reports],
	);
	const fixedPlayerId = edit ? null : searchParams.get("playerId");

	const [report, setReport] = useState(() =>
		edit
			? null
			: {
					reportDate: moment().format("YYYY-MM-DD"),
					values: {},
					match: location.state?.match || null,
					matchId: location.state?.match?.id || null,
					visionId: location.state?.visionId || null,
				},
	);
	const [player, setPlayer] = useState(null);
	useEffect(() => {
		if (edit && data.report)
			setReport((previous) => (previous?.id === id ? previous : data.report));
	}, [edit, id, data.report]);
	const [errors, setErrors] = useState([]);
	const [loading, setLoading] = useState(false);

	const pickPlayer = (picked) => {
		setPlayer(picked);
		setReport((prev) => {
			const template = defaultReportTemplate(templates, picked.goalkeeper);
			return {
				...prev,
				playerId: picked.id,
				player: playerSummary(picked),
				templateId: template?.id,
				template: templateSnapshot(template),
			};
		});
	};

	useEffect(() => {
		const playerId = fixedPlayerId || (edit && report?.playerId);
		if (player?.id === playerId) return;
		if (!playerId || data.status !== "ready" || (!edit && !templates.length))
			return;
		const getPlayer = async () => {
			setLoading(true);
			try {
				const response = await PlayerAPI.getById(playerId);
				if (edit) setPlayer(response);
				else pickPlayer(response);
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		getPlayer();
	}, [fixedPlayerId, edit, report?.playerId, data.status, templates.length]); // eslint-disable-line react-hooks/exhaustive-deps

	// Cambiando template restano i valori dei campi in comune.
	const chooseTemplate = (template) => {
		const snapshot = templateSnapshot(template);
		const ids = new Set(snapshot.fields.map((f) => f.id));
		setReport({
			...report,
			templateId: template.id,
			template: snapshot,
			values: Object.fromEntries(
				Object.entries(report.values || {}).filter(([k]) => ids.has(k)),
			),
		});
	};

	const back = () =>
		navigate(
			edit
				? "/scouting/relazioni/" + id
				: fixedPlayerId
					? "/scouting/players/" + fixedPlayerId
					: "/scouting/relazioni",
		);

	const save = async () => {
		const found = validateReport(report);
		setErrors(found);
		if (found.length) {
			handleNotification("Completa i campi obbligatori", "error");
			return;
		}
		try {
			const saved = await saveReport(report);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/scouting/relazioni/" + saved.id, {replace: true});
		} catch (error) {
			handleNotification(reportError(error), "error");
		}
	};

	const actions = [
		{icon: <ArrowBackIcon />, title: "Annulla", color: "info", onClick: back},
		{
			icon: <SaveIcon />,
			label: "Salva",
			title: "Salva",
			disabled:
				!report?.playerId ||
				!report?.templateId ||
				!data.canWrite ||
				(edit && !data.report?.canEdit),
			onClick: save,
		},
	];

	if (edit && !report)
		return (
			<Page className="page-container relazioni-page">
				<Breadcrumbs
					elements={[{label: "Relazioni", path: "/scouting/relazioni"}]}
				/>
				<div className="relazioni-empty">
					<strong>
						{data.detailLoading
							? "Caricamento relazione…"
							: "Relazione non disponibile"}
					</strong>
				</div>
			</Page>
		);

	return (
		<Page className="page-container relazioni-page">
			<Loading visible={loading || data.loading} />
			<ScoutingReportsStatus data={data} />
			{!edit && data.status === "ready" && !templates.length && (
				<div role="alert">
					Crea o attiva un template nella pagina Template prima di scrivere una
					relazione.
				</div>
			)}
			<Breadcrumbs
				elements={[
					{label: "Relazioni", path: "/scouting/relazioni"},
					{label: "/"},
				]}
				current={edit ? "Modifica" : "Nuova relazione"}
				actions={<MainActions actions={actions} />}
			/>
			<fieldset
				disabled={!data.canWrite || (edit && !data.report?.canEdit)}
				style={{border: 0, padding: 0, margin: 0}}
			>
				<div className="relazioni-stack">
					{!report.playerId ? (
						!loading && (
							<VLFCard
								title="Giocatore"
								openable={false}
								content={
									<PlayerSearch
										onPick={pickPlayer}
										pipeline={pipeline}
										summaries={summaries}
										handleNotification={handleNotification}
									/>
								}
							/>
						)
					) : (
						<>
							<ScoutPlayerTile
								large
								player={player || report.player}
								pipelineEntry={pipeline[report.playerId]}
								reports={summaries[report.playerId]}
								action={
									!edit &&
									!fixedPlayerId && (
										<MainActions
											actions={[
												{
													icon: <SwapHorizIcon />,
													label: "Cambia",
													title: "Cambia giocatore",
													onClick: () => {
														setPlayer(null);
														setReport({
															...report,
															playerId: null,
															player: null,
														});
													},
												},
											]}
										/>
									)
								}
							/>
							<VLFCard
								title="Dati della relazione"
								openable={false}
								content={
									<div className="relazione-meta">
										<div>
											{edit ? (
												<VLFTextField
													label="Template"
													value={report.template?.name}
													editable={false}
												/>
											) : (
												<VLFSelectNew
													label="Template"
													options={templates.map((t) => ({
														value: t.id,
														label: t.name,
													}))}
													value={
														report.templateId
															? {
																	value: report.templateId,
																	label: report.template?.name,
																}
															: null
													}
													onChange={(option) =>
														option &&
														chooseTemplate(
															templates.find((t) => t.id === option.value),
														)
													}
													sorted={false}
													isClearable={false}
													noMargin
												/>
											)}
										</div>
										<div>
											<VLFDatePicker
												label="Data"
												required
												value={
													report.reportDate
														? moment(report.reportDate).toDate()
														: null
												}
												onChange={(date) =>
													setReport({
														...report,
														reportDate: date
															? moment(date).format("YYYY-MM-DD")
															: null,
													})
												}
											/>
										</div>
										{report.match?.name && (
											<div>
												<VLFTextField
													label="Gara"
													value={report.match.name}
													editable={false}
												/>
											</div>
										)}
									</div>
								}
							/>
							<VLFCard
								title="Valutazione"
								openable={false}
								content={
									<ScoutReportForm
										report={report}
										onChange={setReport}
										errors={errors}
									/>
								}
							/>
						</>
					)}
				</div>
			</fieldset>
		</Page>
	);
}

// Una bozza non può attraversare cambi di utente, club o stagione.
export default function HandleRelazionePage(props) {
	useSelector((state) => state.user);
	useSelector((state) => state.token);
	useSelector((state) => state.season?.id);
	return <HandleRelazioneEditor key={getDsContext()} {...props} />;
}
